"use client";

import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { tokenStorage } from "@/lib/auth/token-storage";

const PROACTIVE_THRESHOLD = 60; // seconds before expiry to refresh proactively

export const apiClient = axios.create({
  baseURL: "/",
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// --- Single-flight refresh state ---
let refreshPromise: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  try {
    const res = await axios.post<{ accessToken: string }>(
      "/api/auth/refresh",
      null,
      { withCredentials: true }
    );
    tokenStorage.setAccess(res.data.accessToken);
    return res.data.accessToken;
  } catch {
    tokenStorage.clear();
    return null;
  }
}

// Exported so AuthProvider's mount-time bootstrap (lib/auth/auth-context.tsx)
// shares this single-flight guard instead of calling /api/auth/refresh on
// its own — without that, a query that mounts alongside AuthProvider and
// fires before the access token exists gets a 401, and the response
// interceptor below used to kick off a *second*, uncoordinated refresh
// call. The refresh-token endpoint rotates its jti on every successful
// use with no grace period, so the second call would see the first
// call's already-rotated token and fail with "already rotated" — then
// clear the token the first call had just set successfully, 401-ing
// every subsequent request and bouncing the user to /login even though
// the session was never actually invalid.
export function refreshOnce(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// --- Request interceptor: attach Bearer + proactive refresh ---
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const now = Math.floor(Date.now() / 1000);
  const exp = tokenStorage.getAccessExp();

  if (exp > 0 && exp - now < PROACTIVE_THRESHOLD) {
    await refreshOnce();
  }

  const token = tokenStorage.getAccess();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// --- Response interceptor: handle 401 → refresh + retry ---
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const newToken = await refreshOnce();

      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
    }

    return Promise.reject(error);
  }
);
